/**
 * InGameMenu — slide-up bottom-sheet menu opened from the local player's
 * profile-card hamburger badge (PlayerProfilesOverlay emits `menu:open`).
 *
 * The live game stays visible (dimmed ~50%) behind the sheet, matching the
 * in-game modal pattern in DESIGN.md (slide up from bottom, 400ms,
 * cubic-bezier(0.4, 0, 0.2, 1)).
 *
 * One sheet, three views switched in place:
 *  - main : Chat / Sound / Exit Match launcher rows
 *  - sound: borrows the real Settings audio panel via getSoundPanel
 *           (SettingsScreen.buildAudioPanel) — one implementation + one
 *           settingsStore shared with the Settings tab; returned on close
 *  - exit : "leave the match?" confirm step (leaving is destructive — the
 *           server clean-removes + settles the seat)
 *
 * Chat reuses the existing decoupled StandaloneChat (the chat-bubble surface),
 * so this component only emits `chat`; main.ts owns opening it. `exit` fires
 * once the player confirms leaving.
 *
 * z-index 680: above StandaloneChat (600), below the notification bubble (700).
 */

import { EventEmitter } from '../../engine/core/EventEmitter';
import { sound, SoundId } from '../../engine/audio/sound';

// ─── Constants ────────────────────────────────────────────────────────

const STYLE_ID = 'ingame-menu-styles';

const CHAT_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.6-.8L3 21l1.9-5.7A8.38 8.38 0 0 1 4 11.5 8.5 8.5 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z"/></svg>`;
const MATCH_CHAT_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><line x1="8" y1="9" x2="16" y2="9"/><line x1="8" y1="13" x2="13" y2="13"/></svg>`;
const SOUND_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>`;
const EXIT_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>`;
const BACK_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>`;
const MANUAL_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>`;

type MenuView = 'main' | 'sound' | 'exit';

// ─── Types ────────────────────────────────────────────────────────────

interface InGameMenuEvents {
  open: void;
  close: void;
  /** Player picked "Crew Chat" — main.ts opens the decoupled StandaloneChat. */
  chat: void;
  /** Player picked "Match Chat" — main.ts opens the in-match chat history modal. */
  matchchat: void;
  /** Player picked "How to Play" — main.ts opens the manual to the game's section. */
  manual: void;
  /** Player confirmed "Leave match" — main.ts runs the exit-to-home teardown. */
  exit: void;
}

export interface InGameMenuOptions {
  container: HTMLElement;
  /**
   * Hands back a standalone Sound settings panel (SettingsScreen.buildAudioPanel)
   * to embed in the Sound view. The menu owns the borrowed element only while
   * the sheet is open — it's dropped on close so a fresh, store-synced one is
   * requested next time. Omit to fall back to a "not available" note.
   */
  getSoundPanel?: () => HTMLElement;
}

// ─── Component ────────────────────────────────────────────────────────

export class InGameMenu extends EventEmitter<InGameMenuEvents> {
  public element: HTMLElement;
  private container: HTMLElement;
  private viewsEl: HTMLElement;
  private titleEl: HTMLElement;
  private backBtn: HTMLElement;
  private getSoundPanel?: () => HTMLElement;
  /** Borrowed Sound panel, currently mounted. Released (detached) on close. */
  private soundPanel: HTMLElement | null = null;

  constructor(options: InGameMenuOptions) {
    super();
    this.container = options.container;
    this.getSoundPanel = options.getSoundPanel;
    this.injectStyles();

    this.element = this.createElement();
    this.viewsEl = this.element.querySelector('.ingame-menu-views')!;
    this.titleEl = this.element.querySelector('.ingame-menu-title')!;
    this.backBtn = this.element.querySelector('.ingame-menu-back')!;

    this.container.appendChild(this.element);
    this.wireChrome();
  }

  // ─── Public API ───────────────────────────────────────────────────

  // Open/close are pure class toggles. The element is a single global overlay
  // that lives in the DOM for the whole session (full-screen, fixed), so there
  // is exactly one source of truth — the `visible` class. Closed = no class =
  // transparent + pointer-events:none (click-through) + sheet slid off-screen,
  // all via CSS. No flags, timers, display toggling, or self-heal: nothing can
  // desync, so the badge can never latch dead.

  open(): void {
    this.setView('main');
    this.element.classList.add('visible');
    sound.play(SoundId.SHEET_OPEN);
    this.emit('open', undefined);
  }

  close(): void {
    if (!this.element.classList.contains('visible')) return;
    this.element.classList.remove('visible');
    this.releaseSoundPanel();
    sound.play(SoundId.SHEET_CLOSE);
    this.emit('close', undefined);
  }

  /** Hand the borrowed Sound panel back: detach it and forget the reference. */
  private releaseSoundPanel(): void {
    if (this.soundPanel) {
      this.soundPanel.remove();
      this.soundPanel = null;
    }
  }

  isOpen(): boolean {
    return this.element.classList.contains('visible');
  }

  destroy(): void {
    this.element.remove();
    this.removeAllListeners();
  }

  // ─── DOM ──────────────────────────────────────────────────────────

  private createElement(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'ingame-menu-backdrop';
    // No display toggling — the backdrop stays in the DOM full-time. Closed state
    // (no `visible` class) is transparent + pointer-events:none via CSS, so it's
    // invisible and click-through until opened.
    el.innerHTML = `
      <div class="ingame-menu-sheet" role="dialog" aria-label="Game menu">
        <div class="ingame-menu-grip"></div>
        <div class="ingame-menu-header">
          <button class="ingame-menu-back" type="button" aria-label="Back">${BACK_ICON}</button>
          <span class="ingame-menu-title">Menu</span>
          <button class="ingame-menu-x" type="button" aria-label="Close">&times;</button>
        </div>
        <div class="ingame-menu-views"></div>
      </div>
    `;
    return el;
  }

  private wireChrome(): void {
    // Tap the dimmed backdrop (outside the sheet) to close.
    this.element.addEventListener('pointerdown', (e) => {
      if (e.target === this.element) this.close();
    });
    this.element.querySelector('.ingame-menu-x')!.addEventListener('click', () => this.close());
    this.backBtn.addEventListener('click', () => {
      sound.play(SoundId.UI_BACK);
      this.setView('main');
    });
    this.element.querySelector('.ingame-menu-grip')!.addEventListener('click', () => this.close());
  }

  // ─── View switching ───────────────────────────────────────────────

  private setView(view: MenuView): void {
    // Leaving the Sound view returns the borrowed panel before the DOM swap.
    if (view !== 'sound') this.releaseSoundPanel();
    this.viewsEl.innerHTML = '';
    this.backBtn.classList.toggle('show', view !== 'main');
    if (view === 'main') {
      this.titleEl.textContent = 'Menu';
      this.viewsEl.appendChild(this.buildMainView());
    } else if (view === 'sound') {
      this.titleEl.textContent = 'Sound';
      this.viewsEl.appendChild(this.buildSoundView());
    } else {
      this.titleEl.textContent = 'Leave Match';
      this.viewsEl.appendChild(this.buildExitView());
    }
  }

  private buildMainView(): HTMLElement {
    const list = document.createElement('div');
    list.className = 'ingame-menu-list';
    list.appendChild(this.menuRow(MATCH_CHAT_ICON, 'Match Chat', 'This table’s chat history', 'green', () => {
      this.close();
      this.emit('matchchat', undefined);
    }));
    list.appendChild(this.menuRow(CHAT_ICON, 'Crew Chat', 'Talk with your crew', 'cyan', () => {
      this.close();
      this.emit('chat', undefined);
    }));
    list.appendChild(this.menuRow(SOUND_ICON, 'Sound', 'Music & effects', 'gold', () => {
      this.setView('sound');
    }));
    list.appendChild(this.menuRow(MANUAL_ICON, 'How to Play', 'Rules, dice & wallet', 'purple', () => {
      this.close();
      this.emit('manual', undefined);
    }));
    list.appendChild(this.menuRow(EXIT_ICON, 'Exit Match', 'Cash out and leave', 'red', () => {
      this.setView('exit');
    }));
    return list;
  }

  private menuRow(
    icon: string,
    label: string,
    sub: string,
    accent: 'cyan' | 'gold' | 'red' | 'green' | 'purple',
    onTap: () => void,
  ): HTMLElement {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = `ingame-menu-row accent-${accent}`;
    row.innerHTML = `
      <span class="ingame-menu-row-icon">${icon}</span>
      <span class="ingame-menu-row-text">
        <span class="ingame-menu-row-label">${label}</span>
        <span class="ingame-menu-row-sub">${sub}</span>
      </span>
      <span class="ingame-menu-row-chev">${BACK_ICON}</span>
    `;
    row.addEventListener('click', () => {
      sound.play(SoundId.UI_TAP);
      onTap();
    });
    return row;
  }

  // ─── Sound view ───────────────────────────────────────────────────

  private buildSoundView(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'ingame-menu-sound';
    if (this.getSoundPanel) {
      // Borrow the real Settings audio panel — one source of truth for both the
      // UI and the underlying settingsStore. Returned to its owner on close.
      this.soundPanel = this.getSoundPanel();
      wrap.appendChild(this.soundPanel);
    } else {
      wrap.innerHTML = `<div class="ingame-menu-sound-empty">Sound settings unavailable.</div>`;
    }
    return wrap;
  }

  // ─── Exit view ────────────────────────────────────────────────────

  private buildExitView(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'ingame-menu-exit';
    wrap.innerHTML = `
      <p class="ingame-menu-exit-copy">
        Leave the match? Your seat is cashed out and you'll head back to the block.
      </p>
      <div class="ingame-menu-exit-actions">
        <button class="ingame-menu-btn ghost" type="button" data-act="cancel">Stay</button>
        <button class="ingame-menu-btn danger" type="button" data-act="leave">Leave Match</button>
      </div>
    `;
    wrap.querySelector('[data-act="cancel"]')!.addEventListener('click', () => this.setView('main'));
    wrap.querySelector('[data-act="leave"]')!.addEventListener('click', () => {
      this.close();
      this.emit('exit', undefined);
    });
    return wrap;
  }

  // ─── Styles ─────────────────────────────────────────────────────────

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .ingame-menu-backdrop {
        /* One global, always-mounted overlay. Full-screen, fixed, just below the
           loading screen (z 1000). Closed by default: transparent and
           pointer-events:none so it's invisible AND click-through — it never
           blocks the game or the badge. Opening just adds .visible. */
        position: fixed;
        inset: 0;
        z-index: 950;
        background: rgba(0, 0, 0, 0);
        display: flex;
        align-items: flex-end;
        justify-content: center;
        transition: background 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        pointer-events: none;
      }
      .ingame-menu-backdrop.visible {
        background: rgba(0, 0, 0, 0.5);
        pointer-events: auto;
      }

      .ingame-menu-sheet {
        width: 100%;
        max-width: 480px;
        background: linear-gradient(180deg, #1A1A1F 0%, #111114 100%);
        border: 1px solid rgba(255, 255, 255, 0.06);
        border-bottom: none;
        border-radius: 22px 22px 0 0;
        padding: 10px 18px calc(22px + env(safe-area-inset-bottom, 0px));
        box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.6);
        transform: translateY(100%);
        transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      }
      .ingame-menu-backdrop.visible .ingame-menu-sheet {
        transform: translateY(0);
      }

      .ingame-menu-grip {
        width: 42px;
        height: 4px;
        border-radius: 2px;
        background: rgba(255, 255, 255, 0.22);
        margin: 0 auto 10px;
        cursor: pointer;
      }

      .ingame-menu-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 14px;
      }
      .ingame-menu-back {
        width: 30px;
        height: 30px;
        flex-shrink: 0;
        display: none;
        place-items: center;
        background: rgba(255, 255, 255, 0.05);
        border: none;
        border-radius: 9px;
        color: rgba(255, 255, 255, 0.8);
        cursor: pointer;
        padding: 0;
      }
      .ingame-menu-back.show { display: grid; }
      .ingame-menu-back svg { width: 16px; height: 16px; }

      .ingame-menu-title {
        flex: 1;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 24px;
        letter-spacing: 1px;
        color: #fff;
      }

      .ingame-menu-x {
        width: 30px;
        height: 30px;
        flex-shrink: 0;
        border: none;
        background: rgba(255, 255, 255, 0.05);
        color: rgba(255, 255, 255, 0.55);
        border-radius: 9px;
        font-size: 20px;
        line-height: 1;
        cursor: pointer;
        display: grid;
        place-items: center;
        padding: 0;
      }
      .ingame-menu-x:hover { background: rgba(255, 51, 102, 0.15); color: #FF3366; }

      .ingame-menu-views {
        max-height: 62vh;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
      }

      /* ── Main list ── */
      .ingame-menu-list { display: flex; flex-direction: column; gap: 10px; }

      .ingame-menu-row {
        display: flex;
        align-items: center;
        gap: 14px;
        width: 100%;
        padding: 14px 14px;
        background: rgba(255, 255, 255, 0.03);
        border: 1px solid rgba(255, 255, 255, 0.06);
        border-radius: 14px;
        cursor: pointer;
        text-align: left;
        transition: background 0.15s, border-color 0.15s, transform 0.06s;
        -webkit-tap-highlight-color: transparent;
      }
      .ingame-menu-row:active { transform: scale(0.985); }

      .ingame-menu-row-icon {
        width: 40px; height: 40px;
        flex-shrink: 0;
        display: grid; place-items: center;
        border-radius: 11px;
      }
      .ingame-menu-row-icon svg { width: 21px; height: 21px; }

      .ingame-menu-row-text { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
      .ingame-menu-row-label {
        font-family: 'Rajdhani', sans-serif;
        font-weight: 700;
        font-size: 17px;
        color: #fff;
        line-height: 1.1;
      }
      .ingame-menu-row-sub {
        font-family: 'Rajdhani', sans-serif;
        font-weight: 500;
        font-size: 12px;
        color: rgba(255, 255, 255, 0.45);
      }
      .ingame-menu-row-chev {
        color: rgba(255, 255, 255, 0.3);
        transform: scaleX(-1);
        display: grid; place-items: center;
      }
      .ingame-menu-row-chev svg { width: 16px; height: 16px; }

      .ingame-menu-row.accent-cyan .ingame-menu-row-icon { background: rgba(0, 245, 255, 0.12); color: #00F5FF; }
      .ingame-menu-row.accent-cyan:hover { border-color: rgba(0, 245, 255, 0.4); }
      .ingame-menu-row.accent-gold .ingame-menu-row-icon { background: rgba(255, 215, 0, 0.12); color: #FFD700; }
      .ingame-menu-row.accent-gold:hover { border-color: rgba(255, 215, 0, 0.4); }
      .ingame-menu-row.accent-red .ingame-menu-row-icon { background: rgba(255, 51, 102, 0.12); color: #FF3366; }
      .ingame-menu-row.accent-red:hover { border-color: rgba(255, 51, 102, 0.4); }
      .ingame-menu-row.accent-green .ingame-menu-row-icon { background: rgba(0, 255, 136, 0.12); color: #00FF88; }
      .ingame-menu-row.accent-green:hover { border-color: rgba(0, 255, 136, 0.4); }
      .ingame-menu-row.accent-purple .ingame-menu-row-icon { background: rgba(165, 107, 255, 0.14); color: #A56BFF; }
      .ingame-menu-row.accent-purple:hover { border-color: rgba(165, 107, 255, 0.4); }

      /* ── Sound view (hosts the borrowed Settings audio panel) ── */
      .ingame-menu-sound { padding: 2px 2px 4px; }
      .ingame-menu-sound-empty {
        font-family: 'Rajdhani', sans-serif;
        font-weight: 500;
        font-size: 14px;
        color: rgba(255, 255, 255, 0.4);
        text-align: center;
        padding: 24px 0;
      }

      /* ── Exit view ── */
      .ingame-menu-exit { padding: 2px 2px 4px; }
      .ingame-menu-exit-copy {
        font-family: 'Rajdhani', sans-serif;
        font-weight: 500;
        font-size: 15px;
        line-height: 1.45;
        color: rgba(255, 255, 255, 0.7);
        margin: 0 0 18px;
      }
      .ingame-menu-exit-actions { display: flex; gap: 12px; }
      .ingame-menu-btn {
        flex: 1;
        padding: 13px 0;
        border-radius: 12px;
        border: none;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 18px;
        letter-spacing: 1px;
        cursor: pointer;
        transition: filter 0.15s, transform 0.06s;
      }
      .ingame-menu-btn:active { transform: scale(0.97); }
      .ingame-menu-btn.ghost {
        background: rgba(255, 255, 255, 0.06);
        color: rgba(255, 255, 255, 0.85);
      }
      .ingame-menu-btn.danger {
        background: linear-gradient(180deg, #FF3366 0%, #C81E45 100%);
        color: #fff;
      }
      .ingame-menu-btn.danger:hover { filter: brightness(1.08); }
    `;
    document.head.appendChild(style);
  }
}
